import React, { useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import ReportHeader from "./Report/ReportHeader";
import GradeHero from "./Report/GradeHero";
import KPICards from "./Report/KPICards";
import ExecutiveSummary from "./Report/ExecutiveSummary";
import CategoryScoresChart from "./Report/CategoryScoresChart";
import SectionNavigation from "./Report/SectionNavigation";
import ProviderMetricsSection from "./Report/ProviderMetricsSection";
import BrandRecognitionReport from "./Report/BrandRecognitionReport";
import EeatScoreSection from "./Report/EeatScoreSection";
import {
	MarketCompetitionSection,
	SentimentAnalysisSection,
	AnalysisSummarySection,
} from "./Report/EnhancedReportSections";
import BotCrawlabilitySection from "./Report/BotCrawlabilitySection";
import CitationTrackingSection from "./Report/CitationTrackingSection";
import ProviderComparisonSection from "./Report/ProviderComparisonSection";
import ActionMatrixSection from "./Report/ActionMatrixSection";
import { OverallScoreSummary, ScoreCard } from "./Report/SplitScoreHeader";
import SectionBadge from "./Report/SectionBadge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "./GraderUI";
import { filterAeoActions, filterGeoActions, hasSplitScores } from "./auditUtils";
import { submitLlmsTxtJob } from "../../services/llmsTxtApi";

function buildMatrixFromActions(actions) {
	const list = actions || [];
	return {
		quick_wins: list.filter((a) => a.quadrant === "quick_wins"),
		major_projects: list.filter((a) => a.quadrant === "major_projects"),
		fill_ins: list.filter((a) => a.quadrant === "fill_ins"),
		time_wasters: list.filter((a) => a.quadrant === "time_wasters"),
		all_actions: list,
		statistics: {
			total_actions: list.length,
			quick_wins_count: list.filter((a) => a.quadrant === "quick_wins").length,
			major_projects_count: list.filter((a) => a.quadrant === "major_projects").length,
			fill_ins_count: list.filter((a) => a.quadrant === "fill_ins").length,
			time_wasters_count: list.filter((a) => a.quadrant === "time_wasters").length,
			avg_priority_score:
				list.length > 0
					? list.reduce((sum, a) => sum + (a.priority_score || 0), 0) / list.length
					: 0,
		},
	};
}

export default function AiVisibilityReportView({ report, websiteId, onReportRefresh }) {
	const navigate = useNavigate();
	const [activeTab, setActiveTab] = useState("aeo");
	const [llmsTxtSubmitting, setLlmsTxtSubmitting] = useState(false);

	const handleGenerateLlmsTxt = useCallback(async () => {
		if (!websiteId || llmsTxtSubmitting) return;

		setLlmsTxtSubmitting(true);
		try {
			const result = await submitLlmsTxtJob(websiteId);
			if (!result.ok || !result.job?.id) {
				toast.error(result.message || "Failed to submit job");
				return;
			}
			navigate(
				`/aivisibility/llms-txt/view?jobId=${encodeURIComponent(result.job.id)}`
			);
		} catch {
			toast.error("Failed to submit job");
		} finally {
			setLlmsTxtSubmitting(false);
		}
	}, [websiteId, llmsTxtSubmitting, navigate]);

	const summary = report.summary || {};
	const split = hasSplitScores(summary);
	const deterministicScores = report.deterministic_scores || {};
	const auditId = report.audit?.id;
	const allActions = report.action_matrix?.all_actions || [];
	const aeoActions = filterAeoActions(allActions);
	const geoActions = filterGeoActions(allActions);
	const categoryScores = report.category_scores || [];
	const issues = report.issues || [];
	const recommendations = report.recommendations || [];

	const sections = [
		{ id: "overview", label: "Overview" },
		{ id: "aeo-section", label: "AEO Report" },
		{ id: "geo-section", label: "GEO Report" },
	];

	return (
		<div className="ai-visibility-report-page aeo-grader-report">
			<ReportHeader
				url={report.audit?.url}
				createdAt={report.audit?.created_at}
				completedAt={report.audit?.completed_at}
				status={report.audit?.status}
			/>

			{!split && <SectionNavigation sections={sections} />}

			<div className="grader-report__main">
				{split ? (
					<>
						<OverallScoreSummary
							overallScore={summary.overall_score}
							overallGrade={summary.grade}
							aeoScore={summary.aeo_score}
							geoScore={summary.geo_score}
						/>
						<Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
						<div className="grader-tabs__bar">
							<TabsList>
								<TabsTrigger value="aeo">Answer Engine Optimisation</TabsTrigger>
								<TabsTrigger value="geo">Generative Engine Optimisation</TabsTrigger>
							</TabsList>
						</div>

						<TabsContent value="aeo">
							<div id="aeo-section" className="section-banner section-banner--aeo">
								<p className="section-banner__title">Answer Engine Optimisation (AEO)</p>
								<p className="section-banner__subtitle">
									How well the site is structured for AI retrieval
								</p>
							</div>

							<ScoreCard
								type="aeo"
								grade={summary.aeo_grade || "N/A"}
								score={summary.aeo_score}
								breakdown={summary.aeo_category_breakdown}
								weights={summary.score_weights?.aeo || {}}
								deterministicScores={deterministicScores}
								onGenerateLlmsTxt={handleGenerateLlmsTxt}
								llmsTxtSubmitting={llmsTxtSubmitting}
							/>

							<ExecutiveSummary
								section="aeo"
								overallSummary={summary.executive_summary}
								prioritizedRecommendations={aeoActions}
								providerMetrics={report.provider_metrics}
								topProviderScore={report.provider_metrics?.[0]?.aeo_score}
							/>

							{/* {report.provider_metrics?.length > 0 && (
								<section id="aeo-provider-metrics" className="grader-section">
									<h3 className="grader-section-title">
										AI Provider Evaluation
										<SectionBadge type="aeo" />
									</h3>
									<ProviderMetricsSection
										metrics={report.provider_metrics}
										citationData={report.citation_data}
									/>
								</section>
							)} */}

							{report.crawlability_data && (
								<section id="aeo-bot-crawlability" className="grader-section">
									<h3 className="grader-section-title">
										Bot Crawlability
										<SectionBadge type="aeo" />
									</h3>
									<BotCrawlabilitySection
										data={report.crawlability_data}
										indexabilityData={report.indexability_data}
										citationData={report.citation_data}
									/>
								</section>
							)}

							{report.provider_comparison_data && (
								<section id="aeo-provider-comparison" className="grader-section">
									<ProviderComparisonSection data={report.provider_comparison_data} />
								</section>
							)}

							{aeoActions.length > 0 && (
								<section id="aeo-action-matrix" className="grader-section">
									<ActionMatrixSection
										auditId={auditId}
										title="AEO Action Matrix"
										subtitle="Actions to improve AI retrieval readiness"
										data={buildMatrixFromActions(aeoActions)}
										onGenerateLlmsTxt={handleGenerateLlmsTxt}
										llmsTxtSubmitting={llmsTxtSubmitting}
									/>
								</section>
							)}
						</TabsContent>

						<TabsContent value="geo">
							<div id="geo-section" className="section-banner section-banner--geo">
								<p className="section-banner__title">Generative Engine Optimisation (GEO)</p>
								<p className="section-banner__subtitle">
									How the brand appears in AI-generated responses
								</p>
							</div>

							<ScoreCard
								type="geo"
								grade={summary.geo_grade || "N/A"}
								score={summary.geo_score}
								breakdown={summary.geo_category_breakdown}
								weights={summary.score_weights?.geo || {}}
								deterministicScores={deterministicScores}
								onGenerateLlmsTxt={handleGenerateLlmsTxt}
								llmsTxtSubmitting={llmsTxtSubmitting}
							/>

							<ExecutiveSummary
								section="geo"
								overallSummary={summary.geo_executive_summary}
								prioritizedRecommendations={geoActions}
								providerMetrics={report.provider_metrics}
								citationRate={report.citation_data?.aggregate_metrics?.citation_rate}
							/>

							{report.eeat_report && (
								<section id="geo-eeat-score" className="grader-section">
									<EeatScoreSection report={report.eeat_report} />
								</section>
							)}

							{report.citation_data && (
								<section id="geo-citation-tracking" className="grader-section">
									<h3 className="grader-section-title grader-section-title--lg">
										AI Citation Tracking
										<SectionBadge type="geo" />
									</h3>
									<CitationTrackingSection
										organicData={report.citation_data}
										webSearchData={report.citation_data_websearch}
										websiteId={websiteId}
										auditId={auditId}
										onReportRefresh={onReportRefresh}
									/>
								</section>
							)}

							{report.brand_recognition_report && (
								<section id="geo-brand-recognition" className="grader-section">
									<h3 className="grader-section-title">
										Brand Recognition Analysis
										<SectionBadge type="shared" />
									</h3>
									<BrandRecognitionReport
										report={report.brand_recognition_report}
										entitySignals={report.entity_signals}
									/>
								</section>
							)}

							{report.market_competition_report && (
								<section id="geo-market-competition" className="grader-section">
									<MarketCompetitionSection report={report.market_competition_report} />
								</section>
							)}

							{report.sentiment_analysis_report && (
								<section id="geo-sentiment-details" className="grader-section">
									<SentimentAnalysisSection report={report.sentiment_analysis_report} />
								</section>
							)}

							{geoActions.length > 0 && (
								<section id="geo-action-matrix" className="grader-section">
									<ActionMatrixSection
										auditId={auditId}
										title="GEO Action Matrix"
										subtitle="Actions to improve brand presence in AI-generated responses"
										data={buildMatrixFromActions(geoActions)}
										onGenerateLlmsTxt={handleGenerateLlmsTxt}
										llmsTxtSubmitting={llmsTxtSubmitting}
									/>
								</section>
							)}
						</TabsContent>
					</Tabs>
					</>
				) : (
					<>
						<section id="overview" className="grader-section">
							{summary.grade != null ? (
								<GradeHero
									score={summary.overall_score}
									grade={summary.grade}
									gradeLabel={summary.grade_label}
									confidenceScore={summary.confidence_score}
									confidenceLabel={summary.confidence_label}
								/>
							) : categoryScores.length > 0 ? (
								<GradeHero
									score={categoryScores.reduce((sum, cs) => sum + (cs.weighted_score || 0), 0)}
									grade="N/A"
									gradeLabel="Summary not available - showing category scores"
									confidenceScore={0.5}
									confidenceLabel="medium"
								/>
							) : null}

							<div className="mt-8">
								<KPICards
									totalPages={categoryScores.length > 0 ? 20 : 0}
									corePages={4}
									issueCount={issues.length}
									recommendationCount={recommendations.length}
									topStrengths={summary.top_strengths}
									topWeaknesses={summary.top_weaknesses}
								/>
							</div>
						</section>

						<ExecutiveSummary
							overallSummary={summary.executive_summary}
							prioritizedRecommendations={allActions}
							providerMetrics={report.provider_metrics}
							citationRate={report.citation_data?.aggregate_metrics?.citation_rate}
							citationData={report.citation_data}
						/>

						{report.provider_metrics?.length > 0 && (
							<section id="provider-metrics" className="grader-section">
								<ProviderMetricsSection
									metrics={report.provider_metrics}
									citationData={report.citation_data}
								/>
							</section>
						)}

						{categoryScores.length > 0 && (
							<section id="category-scores" className="grader-section">
								<CategoryScoresChart scores={categoryScores} />
							</section>
						)}

						{report.brand_recognition_report && (
							<section id="brand-recognition" className="grader-section">
								<BrandRecognitionReport
									report={report.brand_recognition_report}
									entitySignals={report.entity_signals}
								/>
							</section>
						)}

						{report.market_competition_report && (
							<section id="market-competition" className="grader-section">
								<MarketCompetitionSection report={report.market_competition_report} />
							</section>
						)}

						{report.sentiment_analysis_report && (
							<section id="sentiment-details" className="grader-section">
								<SentimentAnalysisSection report={report.sentiment_analysis_report} />
							</section>
						)}

						{report.analysis_summary_report && (
							<section id="analysis-summary" className="grader-section">
								<AnalysisSummarySection report={report.analysis_summary_report} />
							</section>
						)}

						{report.crawlability_data && (
							<section id="bot-crawlability" className="grader-section">
								<BotCrawlabilitySection
									data={report.crawlability_data}
									indexabilityData={report.indexability_data}
									citationData={report.citation_data}
								/>
							</section>
						)}

						{report.citation_data && (
							<section id="citation-tracking" className="grader-section">
								<CitationTrackingSection
									organicData={report.citation_data}
									webSearchData={report.citation_data_websearch}
									websiteId={websiteId}
									auditId={auditId}
									onReportRefresh={onReportRefresh}
								/>
							</section>
						)}

						{report.provider_comparison_data && (
							<section id="provider-comparison" className="grader-section">
								<ProviderComparisonSection data={report.provider_comparison_data} />
							</section>
						)}

						{report.action_matrix && (
							<section id="action-matrix" className="grader-section">
								<ActionMatrixSection
									auditId={auditId}
									data={report.action_matrix}
									onGenerateLlmsTxt={handleGenerateLlmsTxt}
									llmsTxtSubmitting={llmsTxtSubmitting}
								/>
							</section>
						)}
					</>
				)}
			</div>
		</div>
	);
}
